import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import apiClient from "../lib/api-client";
import { useAuth } from "./AuthContext";
import {
  SUBSCRIPTION_QUERY_KEY,
  fetchSubscriptionSnapshot,
} from "@/hooks/useSubscription";

export interface Location {
  id: string;
  name: string;
  slug: string;
  logo: string | null;
  coverImage: string | null;
  reservationsPausedUntil?: string | null;
  // Módulo Clases (add-on): pinta el menú "Clases" del sidebar.
  classesEnabled?: boolean;
  address: string;
  city: string;
  state: string;
  country: string;
  postalCode: string | null;
  phone: string;
  email: string;
  timezone: string;
  isActive: boolean;
  showInApp: boolean;
  whatsappEnabled?: boolean;
  /** WhatsApp IA (FASE 3b): número propio de la sede para el bot + handoff humano. */
  whatsappAiEnabled?: boolean;
  whatsappPhoneNumberId?: string | null;
  whatsappPhoneNumber?: string | null;
  /** Timers configurables del inbox de conversaciones (ver `Conversations.tsx` → modal de ajustes). */
  whatsappHumanTimeoutHours?: number;
  whatsappAutoCloseDays?: number | null;
  createdAt: string;
  organizationId?: string;
  organization?: {
    id: string;
    name: string;
    slug: string;
    subscriptionStatus: string;
    bookingMode?: "tables" | "appointments";
    /**
     * Último acceso de CUALQUIERA de ese negocio, como mucho un elemento.
     * Vacío = nunca ha entrado. Solo lo manda `/admin/all-locations`, es
     * decir, solo lo ve el superadmin (19-sep-2026).
     */
    users?: Array<{ lastLoginAt: string | null }>;
  };
  _count?: {
    reservations: number;
    /**
     * Citas de la sede. Las reservas de mesa y las citas viven en modelos
     * distintos: un centro en modo "appointments" tiene SIEMPRE 0 reservas, así
     * que hay que mirar este contador para no enseñarle un 0 falso (19-sep-2026).
     */
    appointments?: number;
  };
}

interface LocationContextType {
  /** Lista de todos los restaurantes del usuario */
  locations: Location[];
  /** Restaurante actualmente seleccionado */
  currentLocation: Location | null;
  /** ID del restaurante actual (para facilitar uso) */
  currentLocationId: string | null;
  /** Cambiar el restaurante activo */
  setCurrentLocation: (location: Location) => void;
  /** Cambiar por ID */
  setCurrentLocationById: (id: string) => void;
  /** Recargar lista de restaurantes */
  refreshLocations: () => Promise<void>;
  /** Estado de carga */
  isLoading: boolean;
  /** Error si existe */
  error: string | null;
  /** Si puede crear más restaurantes según su plan */
  canCreateLocation: boolean;
  /** Límite de restaurantes del plan */
  maxLocations: number;
  /** Version counter - cambia cada vez que se cambia de ubicación (para forzar refetch) */
  locationVersion: number;
  /**
   * bookingMode EFECTIVO a mostrar en la UI ("tables" | "appointments").
   * - Superadmin: el de la organización de `currentLocation` (la que está impersonando).
   * - Usuario normal: el de su propia organización (AuthContext).
   * Usar SIEMPRE este valor (o el hook `useBookingMode()`) en vez de
   * `user?.organization?.bookingMode` para decidir qué secciones/copys mostrar.
   */
  currentBookingMode: "tables" | "appointments";
}

const LocationContext = createContext<LocationContextType | undefined>(
  undefined,
);

const STORAGE_KEY = "rm_current_location_id";
const ORG_STORAGE_KEY = "rm_current_organization_id";

export function LocationProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [locations, setLocations] = useState<Location[]>([]);
  const [currentLocation, setCurrentLocationState] = useState<Location | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [maxLocations, setMaxLocations] = useState(1);
  const [locationVersion, setLocationVersion] = useState(0);

  const isSuperAdmin = user?.role === "owner";
  const queryClient = useQueryClient();

  /**
   * La suscripción se pide a través de la cache de React Query en vez de con
   * una llamada propia: `useSubscription` ya la trae para los banners del
   * Layout, así que de otro modo esta sería una petición duplicada más a
   * `/organizations/me/subscription` en cada carga del dashboard.
   */
  const getSubscription = useCallback(
    () =>
      queryClient.fetchQuery({
        queryKey: SUBSCRIPTION_QUERY_KEY,
        queryFn: fetchSubscriptionSnapshot,
      }),
    [queryClient],
  );

  const fetchLocations = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setLocations([]);
      setCurrentLocationState(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      let fetchedLocations: Location[] = [];

      if (isSuperAdmin) {
        // Superadmin: fetch ALL locations from all organizations
        const [allLocationsRes, subscription] = await Promise.all([
          apiClient.get("/admin/all-locations"),
          getSubscription(),
        ]);
        const all: Location[] = allLocationsRes.data.data || [];
        // Filter out locations from suspended/cancelled organizations
        fetchedLocations = all.filter(
          (l) =>
            !["suspended", "cancelled"].includes(
              l.organization?.subscriptionStatus ?? "",
            ),
        );

        setMaxLocations(subscription.features.maxLocations);
      } else {
        // Regular user: fetch only their organization's locations
        const [locationsRes, subscription] = await Promise.all([
          apiClient.get("/locations"),
          getSubscription(),
        ]);
        fetchedLocations = locationsRes.data.data || [];

        setMaxLocations(subscription.features.maxLocations);
      }

      setLocations(fetchedLocations);

      // Restore last selected location from localStorage
      const savedLocationId = localStorage.getItem(STORAGE_KEY);
      let selectedLocation: Location | null = null;

      if (savedLocationId) {
        selectedLocation =
          fetchedLocations.find((l: Location) => l.id === savedLocationId) ||
          null;
        // If saved location no longer exists (org was suspended/deleted), clear stale keys
        if (!selectedLocation) {
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(ORG_STORAGE_KEY);
        }
      }

      // If no saved location or saved one doesn't exist anymore, select the first one
      if (!selectedLocation && fetchedLocations.length > 0) {
        selectedLocation = fetchedLocations[0];
      }

      setCurrentLocationState(selectedLocation);

      // Persist selection
      if (selectedLocation) {
        localStorage.setItem(STORAGE_KEY, selectedLocation.id);
        // For superadmin, also persist the organization ID for context switching
        if (isSuperAdmin && selectedLocation.organizationId) {
          localStorage.setItem(
            ORG_STORAGE_KEY,
            selectedLocation.organizationId,
          );
        }
      }
    } catch (err) {
      console.error("Error fetching locations:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Error al cargar restaurantes";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user, isSuperAdmin, getSubscription]);

  // Fetch locations when user is authenticated
  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const setCurrentLocation = useCallback(
    (location: Location) => {
      setCurrentLocationState(location);
      localStorage.setItem(STORAGE_KEY, location.id);
      // For superadmin, update organization context
      if (isSuperAdmin && location.organizationId) {
        localStorage.setItem(ORG_STORAGE_KEY, location.organizationId);
      }
      // Increment version to trigger refetch in components
      setLocationVersion((v) => v + 1);
    },
    [isSuperAdmin],
  );

  const setCurrentLocationById = useCallback(
    (id: string) => {
      const location = locations.find((l) => l.id === id);
      if (location) {
        setCurrentLocation(location);
      }
    },
    [locations, setCurrentLocation],
  );

  const refreshLocations = useCallback(async () => {
    await fetchLocations();
  }, [fetchLocations]);

  const canCreateLocation = locations.length < maxLocations;

  // bookingMode efectivo: para superadmin sigue a la organización impersonada
  // (currentLocation), no a la propia cuenta del superadmin.
  const currentBookingMode: "tables" | "appointments" = isSuperAdmin
    ? (currentLocation?.organization?.bookingMode ?? "tables")
    : (user?.organization?.bookingMode ?? "tables");

  return (
    <LocationContext.Provider
      value={{
        locations,
        currentLocation,
        currentLocationId: currentLocation?.id || null,
        setCurrentLocation,
        setCurrentLocationById,
        refreshLocations,
        isLoading,
        error,
        canCreateLocation,
        maxLocations,
        locationVersion,
        currentBookingMode,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
}

export function useLocation() {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error("useLocation must be used within a LocationProvider");
  }
  return context;
}

/**
 * Hook de conveniencia para obtener solo el bookingMode EFECTIVO ("tables" | "appointments").
 * Sigue a la organización impersonada cuando el usuario es superadmin; a la propia
 * organización del usuario en caso contrario. Ver `currentBookingMode` en LocationContext.
 */
export function useBookingMode(): "tables" | "appointments" {
  return useLocation().currentBookingMode;
}
